import { cn } from '@/lib/utils';
import { useMemo } from 'react';
import type { PaceZoneDefinition } from '@/lib/analyticsApi';
import { useUnitSystem } from '@/hooks/useUnitSystem';

interface ZoneData {
  zone: string;
  name?: string;
  seconds?: number;
  minutes: number;
  percentage: number;
}

interface PaceZoneChartProps {
  paceZones: ZoneData[];
  zoneDefinitions?: PaceZoneDefinition[];
  thresholdPaceSource?: 'configured' | 'inferred' | null;
  className?: string;
}

/** 5-zone color palette: teal → cyan → amber → orange → red */
const ZONE_COLORS: Record<string, string> = {
  Z1: 'hsl(170, 80%, 45%)',
  Z2: 'hsl(185, 70%, 50%)',
  Z3: 'hsl(50, 80%, 55%)',
  Z4: 'hsl(35, 85%, 55%)',
  Z5: 'hsl(15, 85%, 55%)',
};

const DEFAULT_ZONE_NAMES: Record<string, string> = {
  Z1: 'Easy',
  Z2: 'Aerobic',
  Z3: 'Tempo',
  Z4: 'Threshold',
  Z5: 'VO2max',
};

/** Format min/km float to M:SS string (e.g., 5.5 → "5:30") */
const formatPaceRaw = (minPerKm: number): string => {
  const minutes = Math.floor(minPerKm);
  const seconds = Math.round((minPerKm - minutes) * 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export function PaceZoneChart({ paceZones, zoneDefinitions, thresholdPaceSource, className }: PaceZoneChartProps) {
  const { formatPaceRange, paceUnit } = useUnitSystem();

  const defsByZone = useMemo(() => {
    const map = new Map<string, PaceZoneDefinition>();
    if (zoneDefinitions) {
      for (const def of zoneDefinitions) map.set(def.zone, def);
    }
    return map;
  }, [zoneDefinitions]);

  const totalPct = paceZones.reduce((s, z) => s + z.percentage, 0);
  const hasData = paceZones.length > 0 && totalPct > 0;

  // Build all 5 zones for the legend, even ones with 0%
  const allZones = useMemo(() => {
    const zoneLabels = ['Z1', 'Z2', 'Z3', 'Z4', 'Z5'];
    const dataByZone = new Map(paceZones.map((z) => [z.zone, z]));

    return zoneLabels.map((label) => {
      const data = dataByZone.get(label);
      const def = defsByZone.get(label);
      // Build pace range as min/km M:SS string — converted to user unit in render
      const rawRange = def ? buildPaceRangeRaw(def) : null;
      return {
        zone: label,
        name: data?.name || def?.name || DEFAULT_ZONE_NAMES[label] || label,
        percentage: data?.percentage ?? 0,
        minutes: data?.minutes ?? 0,
        color: ZONE_COLORS[label] || 'hsl(0, 0%, 40%)',
        paceRange: rawRange ? `${formatPaceRange(rawRange)}${paceUnit}` : null,
      };
    });
  }, [paceZones, defsByZone, formatPaceRange, paceUnit]);

  return (
    <div className={cn(
      'p-4 rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[20px]',
      'shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]',
      className
    )}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-1 h-1 rounded-full bg-[hsl(200,65%,55%)]" />
        <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-muted-foreground">
          PACE ZONE DISTRIBUTION
        </span>
        {thresholdPaceSource && (
          <span className="text-[8px] font-mono tracking-wider uppercase text-muted-foreground/50 ml-auto">
            {thresholdPaceSource === 'inferred' ? 'auto-detected' : 'manual'}
          </span>
        )}
      </div>

      {!hasData ? (
        <div className="py-6 text-center">
          <p className="text-[11px] font-mono text-muted-foreground/60">
            No pace zone data available yet
          </p>
          <p className="text-[10px] font-mono text-muted-foreground/40 mt-1">
            Sync running activities with stream data to see distribution
          </p>
        </div>
      ) : (
        <>
          {/* Stacked horizontal bar */}
          <div className="h-7 rounded-md overflow-hidden flex bg-white/[0.03] mb-4">
            {allZones.map((zone) => (
              zone.percentage > 0 && (
                <div
                  key={zone.zone}
                  className="h-full transition-all duration-700 relative group"
                  style={{
                    width: `${(zone.percentage / totalPct) * 100}%`,
                    backgroundColor: zone.color,
                    opacity: 0.85,
                  }}
                >
                  {zone.percentage >= 10 && (
                    <span className="absolute inset-0 flex items-center justify-center text-[9px] font-mono font-medium text-white/80">
                      {zone.zone}
                    </span>
                  )}
                </div>
              )
            ))}
          </div>

          {/* Zone legend — responsive grid */}
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-x-2 gap-y-2.5">
            {allZones.map((zone) => (
              <div
                key={zone.zone}
                className={cn(
                  'text-center transition-opacity',
                  zone.percentage === 0 && 'opacity-30',
                )}
              >
                <div className="flex items-center justify-center gap-1 mb-0.5">
                  <div
                    className="w-2 h-2 rounded-sm"
                    style={{ backgroundColor: zone.color, opacity: 0.85 }}
                  />
                  <span className="text-[9px] font-mono text-muted-foreground">{zone.zone}</span>
                </div>
                <span className="text-xs font-mono tabular-nums text-foreground block">
                  {zone.percentage > 0 ? `${zone.percentage}%` : '—'}
                </span>
                <span className="text-[8px] font-mono text-muted-foreground/60 block leading-tight">
                  {zone.name}
                </span>
                {zone.paceRange && (
                  <span className="text-[7px] font-mono text-muted-foreground/40 block leading-tight mt-0.5">
                    {zone.paceRange}
                  </span>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Build a pace range string in min/km M:SS format (for conversion by formatPaceRange). */
function buildPaceRangeRaw(def: PaceZoneDefinition): string {
  const min = def.min_pace_min_per_km;
  const max = def.max_pace_min_per_km;

  if (min != null && max != null) {
    return `${formatPaceRaw(min)}–${formatPaceRaw(max)}`;
  }
  if (min != null && max == null) {
    // Z1: slower than max_pace → show "> X:XX"
    return `> ${formatPaceRaw(min)}`;
  }
  if (min == null && max != null) {
    // Z5: faster than min_pace → show "< X:XX"
    return `< ${formatPaceRaw(max)}`;
  }
  return '';
}
